import { RootState, store } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { setBrandIdx } from 'modules/Editor/store/editorSettingsSlice';
import useSWR, { mutate } from 'swr';

import { BrandConfig, BrandState } from '../brand.types';

export const BRAND_INFO_API = '/brand/fetch';
const BRAND_CONFIG_API = '/brand/fetch_brand_config';
const fetcher = (url: string) =>
  fetch(url)
    .then((res) => res.json())
    .then((brandResp) => {
      // If the current brand is not in the list of brands (i.e. was deleted), set the brand to the first one
      const currentBrandIdx = selectBrandIdx(store.getState() as RootState);
      if (Array.isArray(brandResp) && !brandResp?.[currentBrandIdx]) {
        store.dispatch(setBrandIdx(0));
      }

      return brandResp;
    });

type BrandError = {
  status: string;
  message: string;
};

interface UseBrandReturn {
  data: BrandState[] | undefined;
  error: Error;
  brandConfig: BrandConfig | undefined;
  refresh: () => void;
}

interface UseBrandProps {
  brandId?: string;
}

const swrConfig = {
  revalidateIfStale: false,
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
};

export const useBrand = ({ brandId }: UseBrandProps = {}): UseBrandReturn => {
  const { data, error } = useSWR<BrandState[] | BrandError>(BRAND_INFO_API, fetcher, swrConfig);

  const retData = Array.isArray(data) ? data : undefined;
  const retError = typeof data === 'object' && !Array.isArray(data) ? new Error(data.message) : error;

  const brandConfigUrl = brandId ? `${BRAND_CONFIG_API}?id=${brandId}` : null;
  const { data: brandConfig } = useSWR<BrandConfig>(brandConfigUrl, fetcher, swrConfig);

  const refresh = () => {
    mutate(BRAND_INFO_API);
    mutate(brandConfigUrl);
  };

  return { data: retData, error: retError, brandConfig, refresh };
};
